import React, { useEffect, useRef, useState } from 'react';
import {
    GridComponent,
    ColumnsDirective,
    ColumnDirective,
    Inject,
    Page,
    Edit
} from '@syncfusion/ej2-react-grids';
import { NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { Box, Switch, IconButton, Tooltip, useTheme, useMediaQuery, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

export default function WorkflowList({ onWorkflowSelected }) {
    const [workflows, setWorkflows] = useState({ result: [], count: 0 });
    const [searchInput, setSearchInput] = useState(''); 
    const [searchTerm, setSearchTerm] = useState('');
    const gridRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const navigate = useNavigate();
    const [pageSettings, setPageSettings] = useState({ pageSize: 10, currentPage: 1, totalRecordsCount: 0 });
    const theme = useTheme();
    //const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    //const isSm = useMediaQuery(theme.breakpoints.only('sm'));
    //const isMd = useMediaQuery(theme.breakpoints.only('md'));
    let gridHeight = '100%';
    //if (isXs || isSm || isMd) {
    //    gridHeight = '100%';
    //}

    useEffect(() => {
        fetchWorkflows();
        // eslint-disable-next-line
    }, [pageSettings, searchTerm]);

    async function fetchWorkflows() {
        try {
            const params = new URLSearchParams({
                page: pageSettings.currentPage,
                pageSize: pageSettings.pageSize,
                filter: searchTerm
            });
            const res = await fetch(`/api/workflow?${params.toString()}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (res.ok) {
                const json = await res.json();
                setWorkflows({ result: json.workflows || [], count: json.total || 0 });
            } else {
                setWorkflows({ result: [], count: 0 });
            }
        } catch (err) {
            console.error('Error fetching workflows:', err);
            setWorkflows({ result: [], count: 0 });
        }
    }

    async function updateWorkflow(id, payload) {
        try {
            await fetch(`/api/workflow/${id}`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            setWorkflows((prev) => { return { ...prev, result: prev.result.map((wf) => (wf.id === id ? { ...wf, ...payload } : wf)) }; });
        } catch (err) {
            console.error('Error updating workflow:', err);
        }
    }

    function handleActionComplete(args) {
        if (args.requestType === 'save') {
            const updated = args.data;
            updateWorkflow(updated.id, {
                version: updated.version,
                active: updated.active
            });
        }
    }

    function handleActionBegin(args) {
        if (args.requestType === 'paging') {
            setPageSettings((prev) => ({
                ...prev,
                currentPage: args.currentPage,
            }));
        }
    }

    const ActiveTemplate = (props) => (
        <Switch
            size="small"
            checked={props.active}
            onChange={(e) => updateWorkflow(props.id, { active: e.target.checked })}
        />
    );

    const VersionEditTemplate = (props) => (
        <NumericTextBoxComponent
            value={props.version}
            min={1}
            change={(args) => {
                props.version = args.value;
            }}
        />
    );

    const VersionDisplayTemplate = (props) => <span>{props.version}</span>;

    const DefinitionTemplate = (props) => (
        <Tooltip title="Open workflow">
            <IconButton onClick={() => navigate(`/workflow/editor/${props.id}`)} size="small">
                <VisibilityIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    // Search input handler
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            setPageSettings((prev) => ({ ...prev, currentPage: 1 }));
            setSearchTerm(value);
        }, 500);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                height: '100%',
                width: '100%',
                minHeight: 0
            }}
        >
            <Box
                sx={{
                    width: '80%',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {/* Search input above the grid */}
                <Box sx={{ mb: 2, width: '60%' }}>
                    <TextField
                        label="Search by Name"
                        variant="outlined"
                        size="small"
                        value={searchInput}
                        onChange={handleSearchChange}
                        fullWidth
                    />
                </Box>
                <Box
                    sx={{
                        alignSelf: 'flex-end',
                        alignItems: 'end',
                        mb: 2
                    }}
                >
                    <Tooltip title="Add new workflow">
                        <IconButton onClick={() => navigate('/workflow/editor')}>
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Box
                sx={{
                    width: '80%',
                    minHeight: 350,
                    height: { xs: 'calc(100vh - 180px)', sm: 'calc(100vh - 180px)' },
                    maxHeight: { xs: 'calc(100vh - 180px)', sm: 'calc(100vh - 180px)' },
                }}
            >

                <GridComponent
                    id='workflowGrid'
                    ref={gridRef}
                    dataSource={workflows}
                    actionComplete={handleActionComplete}
                    actionBegin={handleActionBegin}
                    editSettings={{ allowEditing: false, mode: 'Normal' }}
                    pageSettings={pageSettings}
                    allowPaging={true}
                    width="100%"
                    {...(gridHeight ? { height: gridHeight } : {})}
                >
                    <ColumnsDirective>
                        <ColumnDirective
                            field="name"
                            headerText="Name"
                            width="200"
                            allowEditing={false}
                        />
                        <ColumnDirective
                            field="version"
                            headerText="Version"
                            width="100"
                            editType="numericedit"
                            editTemplate={VersionEditTemplate}
                            template={VersionDisplayTemplate}
                        />
                        <ColumnDirective
                            field="active"
                            headerText="Active"
                            width="100"
                            template={ActiveTemplate}
                        />
                        <ColumnDirective
                            headerText="Definition"
                            width="100"
                            template={DefinitionTemplate}
                            allowEditing={false}
                        />
                        <ColumnDirective
                            headerText='Test'
                            width='100'
                            template={(props) => (
                                <IconButton onClick={() => navigate(`/workflow/trigger/${props.id}`)}>
                                    <PlayArrowIcon />
                                </IconButton>
                            )}
                            allowEditing={false}
                        />
                    </ColumnsDirective>
                    <Inject services={[Page, Edit]} />
                </GridComponent>
            </Box>
        </Box>
    );
}
